import React, { useState, useEffect, useRef } from 'react';
import { useSOWDataStore, useTranslation } from '../../store';
import Loading from '../Loading';
import LoadingError from '../LoadingError';
import NoData from '../NoData';
import { queryParamsService } from '../../services/queryParams';

import '../../css/Location.css';

const LocationTab = () => {
  const { sowData, sowLoading, sowError, fetchSOWData } = useSOWDataStore();
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [map, setMap] = useState(null);
  const mapRef = useRef(null);
  const mapInitializedRef = useRef(false);
  const { t } = useTranslation();

  // Load Leaflet CSS and JS
  useEffect(() => {   
    const fetchData = async () => {
      queryParamsService.initialize();
      await fetchSOWData();
    };
    fetchData();

    // Load Leaflet CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    link.crossOrigin = '';
    document.head.appendChild(link);

    // Load Leaflet JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    script.crossOrigin = '';
    script.async = true;
    script.onload = () => {
      setMapLoaded(true);
    };
    script.onerror = () => console.error('Leaflet script failed to load');
    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (map) {
        map.remove();
      }
    };
  }, []);

  // Initialize map when script is loaded and DOM is ready
  useEffect(() => {
    if (mapLoaded && sowData?.customer && mapRef.current && !mapInitializedRef.current) {
      initMap();
      mapInitializedRef.current = true;
    }
  }, [mapLoaded, sowData, mapRef.current]);

  // Update map when user location changes
  useEffect(() => {
    if (map && userLocation) {
      updateMapWithUserLocation();
    }
  }, [userLocation, map]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      mapInitializedRef.current = false;
      if (map) {
        map.remove();
      }
    };
  }, []);

  // Get user's current location
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError(t('geolocationNotSupported'));
      return;
    }

    setIsGettingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(newLocation);
        setIsGettingLocation(false);
        
        // Center map on user location
        if (map) {
          map.setView([newLocation.lat, newLocation.lng], 14);
        }
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError(t('userDeniedGeolocation'));
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError(t('locationinfoUnavailable'));
            break;
          case error.TIMEOUT:
            setLocationError(t('locationRequestTimeout'));
            break;
          default:
            setLocationError(t('unknownError'));
            break;
        }
      },
      { 
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      }
    );
  };

  const initMap = () => {
    if (!mapRef.current || !window.L) return;

    try {
      const customerAddress = `${sowData.customer.customerAddress}, ${sowData.customer.customerCity}, ${sowData.customer.customerStateProvince}`;
      const fallbackLocation = [45.4899, -73.7453];

      const mapInstance = window.L.map(mapRef.current).setView(fallbackLocation, 12);

      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(mapInstance);

      setMap(mapInstance);
      
      // Force map to update its size and load all tiles
      setTimeout(() => {
        mapInstance.invalidateSize();
      }, 100);
      
      // Geocode the customer address
      geocodeAddress(mapInstance, customerAddress);
      
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };

  const geocodeAddress = async (mapInstance, address) => {
    if (!address) return;

    try {
      // Use Nominatim for geocoding (OpenStreetMap's geocoding service)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        const location = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
        
        // Center map on customer location
        mapInstance.setView(location, 14);
        
        // Add customer marker
        window.L.marker(location)
          .addTo(mapInstance)
          .bindPopup(`<strong>${sowData.customer.customerName}</strong><br>${address}`)
          .openPopup();
          
        // Add custom icon for customer (red)
        const customerIcon = window.L.divIcon({
          className: 'custom-marker customer-marker',
          html: '📍',
          iconSize: [30, 30],
          iconAnchor: [15, 30]
        });
        
        window.L.marker(location, { icon: customerIcon })
          .addTo(mapInstance)
          .bindPopup(`<strong>${sowData.customer.customerName}</strong><br>${address}`);
          
      } else {
        console.warn('Geocode was not successful for address:', address);
        // Fallback to default marker at current center
        const currentCenter = mapInstance.getCenter();
        window.L.marker(currentCenter)
          .addTo(mapInstance)
          .bindPopup(t('aproxCustomerLocation'));
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
    }
  };

  const updateMapWithUserLocation = () => {
    if (!userLocation || !map) return;

    // Add user location marker with custom icon
    const userIcon = window.L.divIcon({
      className: 'custom-marker user-marker',
      html: '🔵',
      iconSize: [25, 25],
      iconAnchor: [12, 25]
    });

    window.L.marker([userLocation.lat, userLocation.lng], { icon: userIcon })
      .addTo(map)
      .bindPopup(t('yourLocation'))
      .openPopup();

    // Get customer location (try to find existing customer marker or use map center)
    const customerMarker = findCustomerMarker();
    if (customerMarker) {
      const customerLatLng = customerMarker.getLatLng();
      drawRoute(userLocation, { lat: customerLatLng.lat, lng: customerLatLng.lng });
    }
  };

  const findCustomerMarker = () => {
    if (!map) return null;
    
    let customerMarker = null;
    map.eachLayer((layer) => {
      if (layer instanceof window.L.Marker && layer.options.icon?.options?.className === 'custom-marker customer-marker') {
        customerMarker = layer;
      }
    });
    return customerMarker;
  };

  const drawRoute = (origin, destination) => {
    if (!window.L) return;

    // Use OSRM for routing (Open Source Routing Machine)
    const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson`;

    fetch(osrmUrl)
      .then(response => response.json())
      .then(data => {
        if (data.code === 'Ok') {
          const routeGeometry = data.routes[0].geometry;
          
          // Convert polyline to LatLng array
          const coordinates = routeGeometry.coordinates.map(coord => [coord[1], coord[0]]);
          
          // Draw the route on the map
          window.L.polyline(coordinates, {
            color: 'blue',
            weight: 4,
            opacity: 0.7,
            dashArray: '5, 10'
          }).addTo(map);
        } else {
          console.warn('Routing request failed:', data.code);
        }
      })
      .catch(error => {
        console.error('Error fetching route:', error);
      });
  };

  const getDirections = () => {
    if (!sowData?.customer) return;
    
    const address = encodeURIComponent(
      `${sowData.customer.customerAddress}, ${sowData.customer.customerCity}, ${sowData.customer.customerStateProvince}`
    );
    
    let url = `https://www.google.com/maps/dir/?api=1&destination=${address}&travelmode=driving`;
    
    if (userLocation) {
      url += `&origin=${userLocation.lat},${userLocation.lng}`;
    }
    
    window.open(url, '_blank');
  };

  const formatDate = (dateArray) => {
    if (!dateArray || dateArray.length < 5) return t('na');
    const [year, month, day, hour, minute] = dateArray;
    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  if (sowLoading) {
    return (
        <Loading message={t('loadingLocation')}/>
    );
  }

  if (sowError) {
    return (
        <LoadingError message={t('errorLoadingLocation', {'error': sowError})}/>
    );
  }

  if (!sowData?.customer) {
    return (
        <NoData/>
    );
  }

  return (
    <div className="tab-content location-tab">
      <div className="location-header">
        <h2>{t('locationInfo')}</h2>
        <p>{t('workOrder')} {sowData.workorderKey}</p>
      </div>

      <div className="location-content">
        <div className="info-sections">
          {/* Customer Information Section */}
          <div className="info-section customer-info">
            <h3>{t('customerInformation')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('Name')}</label>
                <span>{sowData.customer.customerName}</span>
              </div>
              <div className="info-item">
                <label>{t('email')}</label>
                <span>{sowData.customer.customerEmail}</span>
              </div>
              <div className="info-item">
                <label>{t('company')}</label>
                <span>{sowData.customer.customerCompany}</span>
              </div>
              <div className="info-item full-width">
                <label>{t('address')}</label>
                <span>
                  {sowData.customer.customerAddress}, {sowData.customer.customerCity}, {sowData.customer.customerStateProvince}
                </span>
              </div>
            </div>
          </div>

          {/* Maintenance Window Section */}
          <div className="info-section maintenance-info">
            <h3>{t('maintenanceWindow')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('scheduledDate')}</label>
                <span>{formatDate(sowData.maintenanceWindow?.servicingDate)}</span>
              </div>
              <div className="info-item">
                <label>{t('pointOfContact')}</label>
                <span>{sowData.maintenanceWindow?.pointOfContact || t('na')}</span>
              </div>
            </div>
          </div>

          {/* Location Tools Section */}
          <div className="info-section location-tools">
            <h3>{t('locationTools')}</h3>
            <div className="tools-grid">
              <button
                onClick={getUserLocation}
                disabled={isGettingLocation}
                className="location-btn"
              >
                {isGettingLocation ? (
                  <>
                    <span className="spinner"></span>
                    {t('gettingLocation')}
                  </>
                ) : (
                  t('useMyLocation')
                )}
              </button>
              
              <button
                onClick={getDirections}
                disabled={!userLocation}
                className="directions-btn"
                title={!userLocation ? t('enableLocationAccesFirst') : ''}
              >
                {t('getDirections')}
              </button>
            </div>
            
            {locationError && (
              <div className="location-error">
                <strong>{t('error')}</strong> {locationError}
              </div>
            )}
            
            {userLocation && (
              <div className="location-success">
                <strong>{t('locationFound')}</strong>
                <div className="coordinates">
                  Lat: {userLocation.lat.toFixed(6)}, Lng: {userLocation.lng.toFixed(6)}
                </div>
              </div>
            )}

            {!mapLoaded && (
              <div className="map-loading-info">
                {t('loadingMaps')}
              </div>
            )}
          </div>
        </div>

        {/* Maps Container */}
        <div className="map-section">
          <h3>{t('customerLocation')}</h3>
          <div ref={mapRef} className="openstreet-map">
            {!mapLoaded && (
              <div className="map-loading">
                <div className="loading-spinner"></div>
                <p>{t('loadingMap')}</p>
              </div>
            )}
          </div>
          <div className="map-notes">
            <p>
              <strong>{t('note')}</strong> {t('mapNote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationTab;